import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { MODULES, moduleStartNumber } from "../data/program";

const ModuleAccordion = ({ module, startNumber }) => {
    const [open, setOpen] = useState(module.id === 1);
    const panelId = `program-panel-${module.id}`;

    const toggle = () => setOpen((v) => !v);
    const onKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
        }
    };

    return (
        <div
            data-testid={`program-accordion-module-${module.id}`}
            className={`rounded-3xl bg-white transition-all duration-300 overflow-hidden ${
                open ? "border-2 border-ink shadow-card" : "border border-border shadow-soft"
            }`}
        >
            <div
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={toggle}
                onKeyDown={onKeyDown}
                className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5 cursor-pointer select-none hover:bg-cream-deep/60 transition-colors duration-300"
            >
                <span className="font-display shrink-0 text-4xl sm:text-5xl font-extrabold text-ink leading-none w-14">
                    {String(module.id).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                    <h3 className="font-display uppercase text-xl sm:text-2xl font-bold text-ink leading-tight">
                        {module.name}
                    </h3>
                    <p className="mt-1.5 text-sm text-ink-mute">{module.desc}</p>
                    <div className="mt-3 flex flex-wrap gap-x-2.5 gap-y-1.5 text-xs font-bold uppercase tracking-wider text-ink-soft">
                        <span className="rounded-full bg-cream-deep px-3 py-1">{module.range}</span>
                        <span className="rounded-full bg-cream-deep px-3 py-1">
                            {module.topics.length} tematów
                        </span>
                    </div>
                </div>
                <span
                    data-testid={`program-toggle-module-${module.id}`}
                    className={`shrink-0 inline-flex items-center justify-center gap-2 rounded-full font-bold text-sm px-5 min-h-[44px] transition-colors duration-300 pointer-events-none ${
                        open
                            ? "bg-ink text-white"
                            : "border-2 border-ink text-ink"
                    }`}
                >
                    {open ? "Ukryj tematy" : "Pokaż wszystkie tematy"}
                    <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                    />
                </span>
            </div>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        id={panelId}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="bg-[#E9FBF7] border-t border-hl-cyan">
                            <ol
                                data-testid={`program-list-module-${module.id}`}
                                className="px-6 sm:px-8 py-7 grid md:grid-cols-2 gap-x-10 gap-y-4"
                            >
                                {module.topics.map((topic, i) => {
                                    const num = startNumber + i;
                                    return (
                                        <li key={num} className="flex gap-3 items-start">
                                            <span className="font-display shrink-0 text-sm font-extrabold text-ink w-8 pt-0.5">
                                                {String(num).padStart(2, "0")}.
                                            </span>
                                            <p className="text-sm text-ink-soft leading-relaxed">
                                                {topic}
                                            </p>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export const Program = () => (
    <section id="program" data-testid="course-program-section" className="py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading
                kicker="Program kursu"
                title="100 lekcji. 5 obszarów. Konkretna odpowiedź na każde pytanie."
                lead="Kliknij w moduł, żeby rozwinąć pełną listę tematów. Każda lekcja zaczyna się od realnego pytania z pracy z dorosłymi - i kończy gotowym rozwiązaniem."
            />
            <div className="mt-12 space-y-5">
                {MODULES.map((m, i) => (
                    <Reveal key={m.id} delay={i * 0.06}>
                        <ModuleAccordion module={m} startNumber={moduleStartNumber(i)} />
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
